import { supabase } from "./supabase";

// API client that automatically includes auth headers
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  const config: RequestInit = {
    method,
    headers,
    credentials: "include",
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(url, config);

  // If unauthorized, try to refresh the session
  if (response.status === 401 && session) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    if (refreshData.session) {
      headers["Authorization"] = `Bearer ${refreshData.session.access_token}`;
      return fetch(url, { ...config, headers });
    }
  }

  return response;
}

// Convenience methods
export const api = {
  get: (url: string) => apiRequest("GET", url),
  post: (url: string, data?: unknown) => apiRequest("POST", url, data),
  patch: (url: string, data?: unknown) => apiRequest("PATCH", url, data),
  delete: (url: string) => apiRequest("DELETE", url),
};

// Helper to throw on non-ok responses (for React Query)
export async function fetchWithAuth<T>(url: string, options?: RequestInit): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options?.headers || {}),
  };

  if (session?.access_token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: "Request failed" } }));
    throw new Error(error.error?.message || error.message || "Request failed");
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text);
}
